import React, { useState, useEffect, useRef, useMemo } from 'react';

import { formatTime } from '../../utils/time-conversions';
import { useCanvas, useTimelineInteraction } from '../../hooks';
import type {
	CycleData,
	TimelineTheme,
	TooltipPosition,
	OverlapColors,
	ValidationError,
	BilateralDragPreview
} from '../../types';
import {
	TimelineContainer,
	TimelineFrame,
	RulerContainer,
	StyledCanvas,
	TimeLabelsContainer,
	TimeLabel,
	TimeTooltip
} from './styles';

interface TimelineCanvasProps {
	cycles: CycleData[];
	currentCycle: CycleData | null;
	videoDuration: number;
	currentTime: number;
	theme?: TimelineTheme;
	onCycleSelect: (cycleId: string | null) => void;
	onUpdateCyclePosition: (cycleId: string, start: number, end: number) => void;
	onInteractionError?: (error: ValidationError | null) => void;
	onSeek?: (time: number) => void;
	isEditMode?: boolean;
	allowBilateral?: boolean;
	onPauseVideo?: () => void;
	onScrubbingChange?: (isScrubbing: boolean) => void;
	onCycleDragChange?: (isDragging: boolean) => void;
	onCycleHover?: (cycleId: string | null) => void;
	allCycles?: CycleData[];
	bilateralDragPreview?: BilateralDragPreview | null;
	onBilateralDragPreviewChange?: (preview: BilateralDragPreview | null) => void;
	allowOverlap?: boolean;
	overlapColors?: OverlapColors;
	outOfRangeCycleIds?: string[];
	outOfRangeFromTime?: number;
	highlightedCycleIds?: string[];
}

function shouldShowIdentifiedCycleHandles(
	isEditMode: boolean,
	currentCycle: CycleData | null,
	cycles: CycleData[]
): boolean {
	if (!isEditMode || !currentCycle?.identified) {
		return false;
	}

	const hasUnidentifiedCycle = cycles.some((c) => !c.identified);

	return !hasUnidentifiedCycle;
}

export function TimelineCanvas({
	cycles,
	currentCycle,
	videoDuration,
	currentTime,
	theme,
	onCycleSelect,
	onUpdateCyclePosition,
	onInteractionError,
	onSeek,
	isEditMode = false,
	allowBilateral = true,
	onPauseVideo,
	onScrubbingChange,
	onCycleDragChange,
	onCycleHover,
	allCycles,
	bilateralDragPreview,
	onBilateralDragPreviewChange,
	allowOverlap = false,
	overlapColors,
	outOfRangeCycleIds,
	outOfRangeFromTime,
	highlightedCycleIds
}: Readonly<TimelineCanvasProps>) {
	const [isEditingIdentified, setIsEditingIdentified] = useState<boolean>(isEditMode);
	const [isScrubbing, setIsScrubbing] = useState<boolean>(false);
	const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);
	const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>({ x: 0, y: 0, time: 0 });
	const timelineContainerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		setIsEditingIdentified(shouldShowIdentifiedCycleHandles(isEditMode, currentCycle, cycles));
	}, [isEditMode, currentCycle?.id, currentCycle?.identified, cycles]);

	const editingCycleId = currentCycle && (!currentCycle.identified || isEditingIdentified) ? currentCycle.id : null;

	const [snapTimeFromInteraction, setSnapTimeFromInteraction] = useState<number | null>(null);

	const [dragPreviewFromInteraction, setDragPreviewFromInteraction] = useState<{
		cycleId: string;
		start: number;
		end: number;
		isValid: boolean;
	} | null>(null);

	const [hoveredCycleIdFromInteraction, setHoveredCycleIdFromInteraction] = useState<string | null>(null);

	const effectiveDragPreview = useMemo(() => {
		if (dragPreviewFromInteraction) {
			return dragPreviewFromInteraction;
		}

		if (!bilateralDragPreview) {
			return null;
		}

		const pairedCycleInThisTimeline = cycles.find((c) => c.id === bilateralDragPreview.pairedCycleId);

		if (!pairedCycleInThisTimeline) {
			return null;
		}

		return {
			cycleId: pairedCycleInThisTimeline.id,
			start: bilateralDragPreview.start,
			end: bilateralDragPreview.end,
			isValid: bilateralDragPreview.isValid
		};
	}, [dragPreviewFromInteraction, bilateralDragPreview, cycles]);

	const { canvasRef, canvasDimensions } = useCanvas({
		videoDuration,
		cycles,
		currentCycleId: currentCycle?.id || null,
		currentTime,
		theme,
		showHandlesForIdentified: isEditingIdentified,
		isScrubbing,
		snapTime: snapTimeFromInteraction,
		dragPreview: effectiveDragPreview,
		hoveredCycleId: hoveredCycleIdFromInteraction,
		overlapColors,
		outOfRangeCycleIds,
		outOfRangeFromTime,
		highlightedCycleIds
	});

	const interactionHandlers = useTimelineInteraction({
		cycles,
		videoDuration,
		onCycleChange: onUpdateCyclePosition,
		onValidationError: onInteractionError,
		onCycleSelect,
		canvasRef,
		canvasDimensions,
		onEnterEditMode: onCycleSelect,
		onSeek,
		currentTime,
		isEditingIdentified,
		isEditMode,
		editingCycleId,
		onHoverTimeChange: (time: number, x: number, y: number) => {
			setTooltipPosition({ x, y, time });
			setTooltipVisible(true);
		},
		onHoverEnd: () => {
			setTooltipVisible(false);
		},
		allowBilateral,
		onScrubbingChange: (scrubbing: boolean) => {
			setIsScrubbing(scrubbing);
			onScrubbingChange?.(scrubbing);
		},
		onCycleDragChange,
		onPauseVideo,
		allCycles,
		allowOverlap
	});

	useEffect(() => {
		setSnapTimeFromInteraction(interactionHandlers.snapTime);
	}, [interactionHandlers.snapTime]);

	useEffect(() => {
		setDragPreviewFromInteraction(interactionHandlers.dragPreview);
	}, [interactionHandlers.dragPreview]);

	useEffect(() => {
		const preview = interactionHandlers.dragPreview;

		if (!onBilateralDragPreviewChange) {
			return;
		}

		if (!preview) {
			onBilateralDragPreviewChange(null);
			return;
		}

		const draggedCycle = cycles.find((c) => c.id === preview.cycleId);

		if (!draggedCycle?.paired_cycle_id) {
			return;
		}

		onBilateralDragPreviewChange({
			cycleId: preview.cycleId,
			pairedCycleId: draggedCycle.paired_cycle_id,
			start: preview.start,
			end: preview.end,
			isValid: preview.isValid
		});
	}, [interactionHandlers.dragPreview, cycles, onBilateralDragPreviewChange]);
	useEffect(() => {
		setHoveredCycleIdFromInteraction(interactionHandlers.hoveredCycleId);
		onCycleHover?.(interactionHandlers.hoveredCycleId);
	}, [interactionHandlers.hoveredCycleId, onCycleHover]);

	const timeOnTooltip = formatTime(tooltipPosition.time);

	return (
		<TimelineContainer ref={timelineContainerRef}>
			<TimelineFrame>
				<RulerContainer>
					<StyledCanvas
						ref={canvasRef}
						onMouseDown={interactionHandlers.handleMouseDown}
						onMouseMove={interactionHandlers.handleMouseMove}
						onMouseUp={interactionHandlers.handleMouseUp}
						onMouseLeave={interactionHandlers.handleMouseLeave}
						onDragStart={(e) => e.preventDefault()}
					/>
					{!!tooltipVisible && (
						<TimeTooltip $x={tooltipPosition.x} $y={tooltipPosition.y}>
							{timeOnTooltip}
						</TimeTooltip>
					)}
				</RulerContainer>
				<TimeLabelsContainer>
					<TimeLabel>{formatTime(0)}</TimeLabel>
					<TimeLabel>{formatTime(videoDuration)}</TimeLabel>
				</TimeLabelsContainer>
			</TimelineFrame>
		</TimelineContainer>
	);
}
